/**
 * AngelOne SmartAPI WebSocket 2.0 – broker-isolated live market feed service.
 * Protocol reference: https://smartapi.angelbroking.com/docs/WebSocket2
 */

const ROOT_URI = "wss://smartapisocket.angelone.in/smart-stream";

const SUBSCRIBE_ACTION = 1;
const UNSUBSCRIBE_ACTION = 0;
const SNAP_QUOTE_MODE = 3;

const HEARTBEAT_MESSAGE = "ping";
const HEARTBEAT_INTERVAL_MS = 10000;
const STALE_DATA_TIMEOUT_MS = 60000;
const MAX_TOKENS_PER_REQUEST = 50;
const PRICE_DIVISOR = 100;

const EXCHANGE = {
  NSE_CM: 1,
  NSE_FO: 2,
  BSE_CM: 3,
  BSE_FO: 4,
  MCX_FO: 5,
};

class AngelOneWebSocketService {
  constructor() {
    this.ws = null;
    this.clientCode = null;
    this.feedToken = null;
    this.apiKey = null;

    this.heartbeatInterval = null;
    this.staleCheckInterval = null;
    this.reconnectTimeout = null;
    this.connectTimeout = null;

    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
    this.baseReconnectDelayMs = 1000;
    this.maxReconnectDelayMs = 20000;
    this.connectTimeoutMs = 12000;

    this.lastActivityAt = 0;

    /** @type {Map<string, { exchangeType: number, token: string }>} */
    this.subscriptions = new Map();

    this.statusListeners = new Set();
    this.priceListeners = new Set();
    this.errorListeners = new Set();

    this.debugEnabled = false;
  }

  debug(...args) {
    if (!this.debugEnabled) return;
    console.log("[AngelOne-WS]", ...args);
  }

  setDebug(enabled) {
    this.debugEnabled = !!enabled;
  }

  getSharedData() {
    try {
      return JSON.parse(localStorage.getItem("sharedData") || "null");
    } catch (_) {
      return null;
    }
  }

  getSocketState() {
    return this.ws?.readyState;
  }

  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  subscriptionKey(exchangeType, token) {
    return `${exchangeType}|${token}`;
  }

  // -------- Public connect / disconnect --------

  connect() {
    const shared = this.getSharedData();
    const clientCode = String(shared?.ClientCode || "").trim();
    const feedToken = String(shared?.feed_token || "").trim();
    const apiKey = String(shared?.api_key || "").trim();

    if (!clientCode || !feedToken || !apiKey) {
      const err = new Error(
        "Missing ClientCode, feed_token, or api_key in sharedData",
      );
      this.emitError(err);
      this.debug("connect aborted: missing auth");
      return;
    }

    this.clientCode = clientCode;
    this.feedToken = feedToken;
    this.apiKey = apiKey;
    this.shouldReconnect = true;

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.debug("connect skipped: socket already open");
      return;
    }
    if (this.ws?.readyState === WebSocket.CONNECTING) {
      this.debug("connect skipped: socket already connecting");
      return;
    }

    this.openSocket();
  }

  disconnect({ manual = true } = {}) {
    this.shouldReconnect = !manual;
    this.clearTimers();

    if (this.ws) {
      try {
        this.ws.close(1000, manual ? "manual disconnect" : "disconnect");
      } catch (_) {
        /* ignore */
      }
    }

    this.ws = null;
    this.emitStatus({
      type: "disconnected",
      manual,
      readyState: WebSocket.CLOSED,
    });
  }

  // -------- Public subscriptions --------

  subscribeMarketFeed(items = []) {
    const normalized = this.normalizeMarketFeedItems(items);
    if (!normalized.length) return;

    normalized.forEach((item) => {
      this.subscriptions.set(
        this.subscriptionKey(item.exchangeType, item.token),
        item,
      );
    });

    if (this.isConnected()) {
      this.sendSubscribe(normalized);
    } else {
      this.debug("queued subscription", normalized.length);
      this.connect();
    }
  }

  unsubscribeMarketFeed(items = []) {
    const normalized = this.normalizeMarketFeedItems(items);
    if (!normalized.length) return;

    normalized.forEach((item) => {
      this.subscriptions.delete(
        this.subscriptionKey(item.exchangeType, item.token),
      );
    });

    if (this.isConnected()) {
      this.sendUnsubscribe(normalized);
    }
  }

  // -------- Public listeners --------

  onStatus(listener) {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  onPrice(listener) {
    this.priceListeners.add(listener);
    return () => this.priceListeners.delete(listener);
  }

  onError(listener) {
    this.errorListeners.add(listener);
    return () => this.errorListeners.delete(listener);
  }

  // -------- Socket lifecycle --------

  openSocket() {
    this.clearConnectTimeout();
    this.clearReconnectTimeout();

    const params = new URLSearchParams({
      clientCode: this.clientCode,
      feedToken: this.feedToken,
      apiKey: this.apiKey,
    });
    const url = `${ROOT_URI}?${params.toString()}`;

    this.debug("opening socket", { attempt: this.reconnectAttempts + 1 });
    this.ws = new WebSocket(url);
    this.ws.binaryType = "arraybuffer";

    this.emitStatus({
      type: "connecting",
      readyState: this.ws.readyState,
      attempt: this.reconnectAttempts + 1,
    });

    this.connectTimeout = setTimeout(() => {
      if (this.ws?.readyState === WebSocket.CONNECTING) {
        this.debug("connect timeout");
        try {
          this.ws.close();
        } catch (_) {
          /* ignore */
        }
      }
    }, this.connectTimeoutMs);

    this.ws.onopen = () => {
      this.clearConnectTimeout();
      this.reconnectAttempts = 0;
      this.touchActivity();
      this.debug("socket open");
      this.emitStatus({ type: "connected", readyState: this.ws.readyState });
      this.startHeartbeat();
      this.startStaleCheck();
      this.replaySubscriptions();
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };

    this.ws.onerror = (event) => {
      this.debug("socket error");
      this.emitError(event);
    };

    this.ws.onclose = (event) => {
      this.clearConnectTimeout();
      this.stopHeartbeat();
      this.stopStaleCheck();

      this.debug("socket close", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
      this.emitStatus({
        type: "closed",
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });

      if (!this.shouldReconnect) return;
      this.scheduleReconnect();
    };
  }

  scheduleReconnect() {
    const delay = Math.min(
      this.maxReconnectDelayMs,
      this.baseReconnectDelayMs * 2 ** this.reconnectAttempts,
    );
    this.reconnectAttempts += 1;

    this.debug("scheduling reconnect", { attempt: this.reconnectAttempts, delay });
    this.emitStatus({
      type: "reconnecting",
      attempt: this.reconnectAttempts,
      delay,
    });

    this.reconnectTimeout = setTimeout(() => {
      this.openSocket();
    }, delay);
  }

  // -------- Messaging --------

  handleMessage(rawData) {
    if (typeof rawData === "string") {
      if (rawData === "pong") {
        this.touchActivity();
        this.emitStatus({ type: "heartbeat-ack" });
        return;
      }
      this.debug("text message", rawData);
      return;
    }

    const buffer =
      rawData instanceof ArrayBuffer
        ? rawData
        : rawData?.buffer instanceof ArrayBuffer
          ? rawData.buffer
          : null;

    if (!buffer) return;

    this.touchActivity();

    const tick = decodeSnapQuotePacket(buffer);
    if (!tick) return;

    const commonTick = mapSnapQuoteToCommonTick(tick);
    if (!commonTick?.Token) return;

    this.priceListeners.forEach((cb) => {
      try {
        cb(commonTick, tick);
      } catch (_) {
        /* ignore listener errors */
      }
    });
  }

  sendSubscribe(items) {
    this.sendSubscriptionAction(SUBSCRIBE_ACTION, items);
  }

  sendUnsubscribe(items) {
    this.sendSubscriptionAction(UNSUBSCRIBE_ACTION, items);
  }

  sendSubscriptionAction(action, items) {
    if (!this.isConnected() || !items.length) return;

    const chunks = chunkFlatItems(items, MAX_TOKENS_PER_REQUEST);
    chunks.forEach((chunk) => {
      const tokenList = buildTokenListPayload(chunk);
      if (!tokenList.length) return;

      const payload = {
        correlationID: generateCorrelationId(),
        action,
        params: {
          mode: SNAP_QUOTE_MODE,
          tokenList,
        },
      };

      try {
        this.ws.send(JSON.stringify(payload));
        this.debug("subscription sent", {
          action,
          mode: SNAP_QUOTE_MODE,
          groups: tokenList.length,
          tokens: chunk.length,
        });
      } catch (err) {
        this.emitError(err);
      }
    });
  }

  replaySubscriptions() {
    const items = Array.from(this.subscriptions.values());
    if (!items.length) return;
    this.sendSubscribe(items);
    this.debug("replayed subscriptions", items.length);
  }

  // -------- Heartbeat / stale detection --------

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (!this.isConnected()) return;
      try {
        this.ws.send(HEARTBEAT_MESSAGE);
        this.emitStatus({ type: "heartbeat-sent" });
      } catch (err) {
        this.emitError(err);
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  startStaleCheck() {
    this.stopStaleCheck();
    this.staleCheckInterval = setInterval(() => {
      if (!this.isConnected()) return;
      if (!this.subscriptions.size) return;

      const idleMs = Date.now() - this.lastActivityAt;
      if (idleMs < STALE_DATA_TIMEOUT_MS) return;

      this.debug("stale connection – reconnecting", { idleMs });
      this.emitStatus({ type: "stale-reconnect", idleMs });
      try {
        this.ws?.close();
      } catch (_) {
        /* ignore */
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  stopStaleCheck() {
    if (this.staleCheckInterval) {
      clearInterval(this.staleCheckInterval);
      this.staleCheckInterval = null;
    }
  }

  touchActivity() {
    this.lastActivityAt = Date.now();
  }

  // -------- Helpers --------

  normalizeMarketFeedItems(items) {
    return (items || [])
      .map((item) => {
        const exchangeType = Number(
          item.exchangeType ?? item.ExchangeType ?? 0,
        );
        const token = String(
          item.token ?? item.Token ?? item.script_code ?? "",
        ).trim();
        if (!exchangeType || !token) return null;
        return { exchangeType, token };
      })
      .filter(Boolean);
  }

  clearConnectTimeout() {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
  }

  clearReconnectTimeout() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  clearTimers() {
    this.clearConnectTimeout();
    this.clearReconnectTimeout();
    this.stopHeartbeat();
    this.stopStaleCheck();
  }

  emitStatus(payload) {
    this.statusListeners.forEach((cb) => {
      try {
        cb(payload);
      } catch (_) {
        /* ignore */
      }
    });
  }

  emitError(error) {
    this.errorListeners.forEach((cb) => {
      try {
        cb(error);
      } catch (_) {
        /* ignore */
      }
    });
  }
}

// -------- Binary decode (mode 3 = Snap Quote) --------

function readInt64LE(view, offset) {
  if (typeof view.getBigInt64 === "function") {
    return Number(view.getBigInt64(offset, true));
  }
  const low = view.getUint32(offset, true);
  const high = view.getInt32(offset + 4, true);
  return high * 2 ** 32 + low;
}

function parseTokenBytes(bytes, start, end) {
  let token = "";
  for (let i = start; i < end; i += 1) {
    if (bytes[i] === 0) break;
    token += String.fromCharCode(bytes[i]);
  }
  return token;
}

/**
 * Decode AngelOne Snap Quote (mode 3) binary packet.
 * Field offsets per official smartWebSocketV2.py.
 */
export function decodeSnapQuotePacket(buffer) {
  const byteLength =
    buffer instanceof ArrayBuffer ? buffer.byteLength : buffer?.byteLength || 0;
  if (byteLength < 123) return null;

  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  const subscriptionMode = view.getUint8(0);
  if (subscriptionMode !== SNAP_QUOTE_MODE) return null;

  const exchangeType = view.getUint8(1);
  const token = parseTokenBytes(bytes, 2, 27);
  const sequenceNumber = readInt64LE(view, 27);
  const exchangeTimestamp = readInt64LE(view, 35);
  const lastTradedPrice = readInt64LE(view, 43);

  const parsed = {
    subscription_mode: subscriptionMode,
    exchange_type: exchangeType,
    token,
    sequence_number: sequenceNumber,
    exchange_timestamp: exchangeTimestamp,
    last_traded_price: lastTradedPrice,
    ltp: lastTradedPrice / PRICE_DIVISOR,
  };

  if (byteLength >= 123) {
    parsed.volume_trade_for_the_day = readInt64LE(view, 67);
    parsed.high_price_of_the_day = readInt64LE(view, 99);
    parsed.low_price_of_the_day = readInt64LE(view, 107);
    parsed.closed_price = readInt64LE(view, 115);
  }

  return parsed;
}

export function mapSnapQuoteToCommonTick(parsed) {
  if (!parsed?.token) return null;

  const toPrice = (raw) =>
    Number.isFinite(raw) ? raw / PRICE_DIVISOR : 0;

  return {
    Token: String(parsed.token),
    LastRate: toPrice(parsed.last_traded_price),
    PClose: toPrice(parsed.closed_price),
    Volume: Number(parsed.volume_trade_for_the_day) || 0,
    High: toPrice(parsed.high_price_of_the_day),
    Low: toPrice(parsed.low_price_of_the_day),
  };
}

/**
 * Maps AngelOne search/watchlist API fields to UI + PMS fields.
 * API: name = short name (e.g. GOLDPETAL), symbol = trading symbol (e.g. GOLDPETAL30JUN26FUT).
 */
export function buildAngelInstrumentFields(row = {}) {
  const instrumentName = String(row?.name || "").trim();
  const tradingSymbol = String(row?.symbol || "").trim();
  const expiry = String(row?.expiry || "").trim();
  const instrumentType = String(
    row?.instrumenttype || row?.instrumentType || row?.InstrumentType || "",
  ).toUpperCase();

  const isDerivative =
    instrumentType.startsWith("FUT") ||
    instrumentType.startsWith("OPT") ||
    instrumentType.endsWith("FUT") ||
    instrumentType.endsWith("OPT") ||
    (Boolean(expiry) && expiry !== "0" && !expiry.startsWith("0."));

  let fullName = instrumentName || tradingSymbol;
  if (instrumentName && expiry && isDerivative) {
    fullName = `${instrumentName} ${expiry}`;
  } else if (!instrumentName) {
    fullName = tradingSymbol;
  }

  return {
    instrumentName: instrumentName || tradingSymbol,
    tradingSymbol: tradingSymbol || instrumentName,
    fullName,
    expiry,
    instrumentType,
    isDerivative,
  };
}

export function getAngelExchangeTypeFromHolding(holding = {}) {
  const exchSeg = String(holding?.ExchSeg || holding?.exch_seg || "").toUpperCase();
  const exch = String(holding?.Exch || holding?.exch || "").toUpperCase();
  const exchType = String(holding?.ExchType || holding?.exchType || "C").toUpperCase();
  const instrumentType = String(
    holding?.InstrumentType || holding?.instrumenttype || "",
  ).toUpperCase();

  const isDerivative =
    exchType.startsWith("D") ||
    instrumentType.startsWith("FUT") ||
    instrumentType.startsWith("OPT") ||
    /_FO$|FO$/.test(exchSeg);

  const isMcx = exch === "M" || exchSeg.includes("MCX");
  const isBse = exch === "B" || exchSeg.startsWith("BSE");
  const isNse = exch === "N" || exchSeg.startsWith("NSE");

  if (isMcx) return EXCHANGE.MCX_FO;
  if (isBse) return isDerivative ? EXCHANGE.BSE_FO : EXCHANGE.BSE_CM;
  if (isNse) return isDerivative ? EXCHANGE.NSE_FO : EXCHANGE.NSE_CM;

  return EXCHANGE.NSE_CM;
}

function buildTokenListPayload(items) {
  const byExchange = new Map();
  items.forEach(({ exchangeType, token }) => {
    const ex = Number(exchangeType);
    const tok = String(token).trim();
    if (!ex || !tok) return;
    if (!byExchange.has(ex)) byExchange.set(ex, []);
    const list = byExchange.get(ex);
    if (!list.includes(tok)) list.push(tok);
  });

  return Array.from(byExchange.entries()).map(([exchangeType, tokens]) => ({
    exchangeType: Number(exchangeType),
    tokens,
  }));
}

function chunkFlatItems(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function generateCorrelationId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`.slice(
    0,
    10,
  );
}

const angelOneWebSocketService = new AngelOneWebSocketService();

export default angelOneWebSocketService;
